import { useEffect, useMemo, useState } from 'react';
import PrototypeNemoLayout from '@shared/PrototypeNemoLayout';
import { UMFormStyles, UMR } from '../../urbanmap/_components';
import { Toast } from '../proposition-c/ui';
import { CreateProjectWizard } from './CreateProjectWizard';
import { useBootstrap } from './hooks';
import { ProjectHeader } from './ProjectHeader';
import { ProjectMap } from './ProjectMap';
import { ProjectsList } from './ProjectsList';
import { AoParamsDialog } from './AoParamsDialog';
import { InviteDialog } from './InviteDialog';
import type { PlanProject, Role, Stage, Variants } from './types';
import { UrbanMapStage } from './UrbanMapStage';
import { Wireframes } from './Wireframes';

export const meta = {
  title: 'Plan — Créer un projet depuis UrbanMap',
  description:
    "Extraction d'un périmètre UrbanMap en projet Plan figé : hypothèses de l'AO, consommations recalculées, équipe invitée.",
  tags: ['planning', 'network', 'poc'],
  author: 'bs1253',
  status: 'draft',
  updatedAt: '2026-10-29',
};

function readVariants(): Variants {
  const p = new URLSearchParams(window.location.search);
  const pick = (k: string): 'A' | 'B' => (p.get(k) === 'B' ? 'B' : 'A');
  return { form: pick('form'), invite: pick('invite'), impact: pick('impact') };
}

export default function CreationProjetPrototype() {
  const { data, isLoading, isError } = useBootstrap();
  const [stage, setStage] = useState<Stage>('urbanmap');
  const [role, setRole] = useState<Role>('plan');
  const [variants, setVariants] = useState<Variants>(readVariants);
  const [wireframes, setWireframes] = useState(() => new URLSearchParams(window.location.search).get('view') === 'wireframes');
  const [projects, setProjects] = useState<PlanProject[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'params' | 'invite' | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (data && projects.length === 0) setProjects(data.projects);
  }, [data, projects.length]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(null), 3600);
    return () => window.clearTimeout(t);
  }, [toast]);

  const current = useMemo(() => projects.find((p) => p.id === currentId) ?? null, [projects, currentId]);

  const update = (next: PlanProject) => setProjects((list) => list.map((p) => (p.id === next.id ? next : p)));

  const body = () => {
    if (isLoading) return <Centered>Chargement du périmètre UrbanMap…</Centered>;
    if (isError || !data) return <Centered>Impossible de charger les données du périmètre.</Centered>;
    if (wireframes) return <Wireframes variants={variants} onChange={setVariants} />;

    if (stage === 'projects') {
      return (
        <ProjectsList
          projects={projects}
          canCreate={role === 'plan'}
          onBackToUM={() => setStage('urbanmap')}
          onCreate={() => setStage('creating')}
          onOpen={(p) => { setCurrentId(p.id); setStage('project'); }}
        />
      );
    }

    if (stage === 'project' && current) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <ProjectHeader
            project={current}
            lineage={data.lineage}
            showInvite={variants.invite === 'B'}
            onInvite={() => setDialog('invite')}
            onEditParams={() => setDialog('params')}
            onOpenProjects={() => setStage('projects')}
            onBackToUM={() => setStage('urbanmap')}
          />
          <ProjectMap data={data} project={current} />
          {dialog === 'params' && (
            <AoParamsDialog
              data={data}
              params={current.params}
              onClose={() => setDialog(null)}
              onSave={(params) => {
                update({ ...current, params });
                setDialog(null);
                setToast('Hypothèses de l’AO mises à jour — consommations du projet recalculées.');
              }}
            />
          )}
          {dialog === 'invite' && (
            <InviteDialog
              candidates={data.candidates}
              members={current.members}
              onClose={() => setDialog(null)}
              onSave={(members) => {
                update({ ...current, members });
                setDialog(null);
                setToast('Membres du projet mis à jour.');
              }}
            />
          )}
        </div>
      );
    }

    if (stage === 'creating') {
      return (
        <CreateProjectWizard
          data={data}
          variants={variants}
          onCancel={() => setStage('urbanmap')}
          onCreated={(project) => {
            setProjects((list) => [project, ...list]);
            setCurrentId(project.id);
            setStage('project');
            setToast(`Projet « ${project.name} » créé : copie figée de ${project.perimeterLabel}.`);
          }}
        />
      );
    }

    return (
      <UrbanMapStage
        data={data}
        role={role}
        onRoleChange={setRole}
        onCreate={() => setStage('creating')}
        onOpenProjects={() => setStage('projects')}
      />
    );
  };

  return (
    <PrototypeNemoLayout title={meta.title}>
      <UMFormStyles />
      <link rel="stylesheet" href={UMR.fontHref} />
      <div style={{ height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column', fontFamily: UMR.font, color: UMR.text }}>
        <button
          type="button"
          onClick={() => setWireframes((w) => !w)}
          style={{
            position: 'absolute', right: 16, bottom: 16, zIndex: 60, cursor: 'pointer', fontFamily: UMR.font, fontSize: 12,
            padding: '6px 10px', background: UMR.white, border: `1px solid ${UMR.border}`,
          }}
        >
          {wireframes ? 'Voir le prototype' : 'Voir les variantes (basse fidélité)'}
        </button>
        {body()}
      </div>
      {toast && <Toast>{toast}</Toast>}
    </PrototypeNemoLayout>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: UMR.textSecondary, fontSize: 14 }}>
      {children}
    </div>
  );
}
